<template>
  <div>
    <div class="expand-loading" v-loading="loading">
      <div v-if="surveyOffer">
        <p class="text-brand-brown font-semibold text-sm">When a customer selects</p>
        <p class="text-brand-black font-bold text-xl">{{ choice.value }}</p>
        <p class="text-sm mb-2 font-medium text-gray-700 mt-4">…trigger the following offer</p>
      </div>
      <div v-else>
        <p class="text-sm mb-2 font-semibold text-gray-700 mt-4">Choose Offer Type</p>
      </div>
      <div v-if="!offer" class="mt-2 h-32">
        <el-select placeholder="Select Offer" @change="(offerType) => $emit('create', offerType)" :value="null" class="w-full">
          <el-option v-for="item in offerTypes" :key="item.value" :label="item.label" :value="item.value"> </el-option>
        </el-select>
      </div>
      <div v-else class="mt-3">
        <el-select
          class="w-full"
          :value="offer.offerType"
          placeholder="Select"
          @change="(offerType) => (offerType ? $emit('create', offerType) : $emit('delete'))"
        >
          <el-option v-for="item in offerTypes" :key="item.value" :label="item.label" :value="item.value"> </el-option>
        </el-select>
        <div class="flex items-center space-x-1.5 text-brand-brown text-xs mt-2" v-if="offer.offerType === OFFER_TYPE.CONTACT">
          <font-awesome-icon :icon="['fas', 'info-circle']" class="text-brand-medium-brown"></font-awesome-icon>
          <p v-if="offer.offerType === OFFER_TYPE.CONTACT" class="text-xs font-medium">
            Make sure you've added supported for this.
            <a class="text-brand-blue" href="https://docs.churnkey.co/installing-churnkey#b7bb07d7fb4e4a2782e21481ba10d34a" target="_blank"
              >Details here →</a
            >
          </p>
        </div>
        <div class="flex items-center space-x-1.5 text-brand-brown text-xs mt-2" v-if="surveyOffer">
          <font-awesome-icon :icon="['fas', 'times-circle']" class="text-brand-medium-brown"></font-awesome-icon>
          <p class="cursor-pointer font-medium" @click="$emit('delete')">Remove offer from {{ choice.value }}</p>
        </div>
        <div v-if="missingPauseCoupon" class="bg-brand-light-blue px-4 py-2 mt-4 rounded-lg">
          <p class="text-brand-black font-medium text-sm">
            Before you can enable customers to pause their accounts during your cancellation flow, you will need to create a discount in your
            Braintree dashboard with the ID
            <span class="text-green-700 font-bold"> {{ BRAINTREE_PAUSE_DISCOUNT_ID }}</span>
          </p>
          <!-- link to braintree dashboard -->
          <p class="font-bold text-brand-black mt-4 text-sm">Why?</p>
          <p class="text-sm">
            Braintree does not have a "pause" feature. Churnkey can emulate it for you with the CHURNKEY_PAUSE discount. You can make the discount for
            any amount.
          </p>
          <div>
            <el-button type="primary" class="mt-4" @click="reloadCoupons" :loading="reloadingCoupons" :disabled="reloadingCoupons" size="mini">
              Reload Coupons
            </el-button>
            <a href="https://www.braintreegateway.com/login" target="_blank" class="text-brand-blue font-semibold text-sm ml-2"
              >Go to Braintree Dashboard</a
            >
          </div>
        </div>
        <div class="mt-4">
          <p class="text-sm font-semibold mb-2 text-gray-700">What do you want this step to say?</p>
          <el-input
            type="text"
            placeholder="Headline"
            :value="header"
            @input="(header) => $emit('update', { header })"
            maxlength="80"
            show-word-limit
          ></el-input>
          <div style="margin: 20px 0"></div>

          <Editor :placeholder="description" :maxlength="0" @update="(description) => $emit('update', { description })" />
        </div>
        <div v-if="offer.offerType === OFFER_TYPE.REDIRECT">
          <p class="text-sm font-semibold leading-5 text-gray-700 mb-2 mt-4">Redirect to URL</p>
          <el-input
            type="text"
            placeholder="Redirect URL"
            :value="offer.redirectConfig.redirectUrl"
            @input="(redirectUrl) => $emit('update', { redirectConfig: { redirectUrl, redirectLabel: offer.redirectConfig.redirectLabel } })"
          ></el-input>
          <p class="text-sm font-semibold leading-5 text-gray-700 mb-2 mt-4">Button Label</p>
          <el-input
            type="text"
            placeholder="Call to Action"
            :value="offer.redirectConfig.redirectLabel"
            @input="(redirectLabel) => $emit('update', { redirectConfig: { redirectUrl: offer.redirectConfig.redirectUrl, redirectLabel } })"
          ></el-input>
        </div>
        <div v-if="offer.offerType === OFFER_TYPE.DISCOUNT && orgSupportsCoupons">
          <p class="text-sm font-semibold leading-5 text-gray-700 mb-2 mt-4">{{ discountLabel }}</p>
          <coupon-selector :offer="offer" :coupons="coupons" @update="handleCouponSelect"> </coupon-selector>
        </div>
        <div v-if="offer.offerType === OFFER_TYPE.DISCOUNT && !orgSupportsCoupons">
          <p class="text-sm font-semibold leading-5 text-gray-700 mb-2 mt-4">Discount Amount (currency of the subscription)</p>
          <el-input
            type="number"
            placeholder="0.00"
            :value="parseInt(Number(offer.discountConfig.customAmount)) / 100"
            @input="updateCustomAmount"
          ></el-input>
          <p class="text-sm font-semibold leading-5 text-gray-700 mb-2 mt-4">Discount Type</p>
          <el-select :value="offer.discountConfig.customDuration" placeholder="Select" @change="updateCustomDuration" class="w-full">
            <el-option-group>
              <el-option key="once" label="Once" value="ONCE">Once</el-option>
              <el-option key="forever" label="Recurring (Forever)" value="FOREVER">Recurring (Forever)</el-option>
            </el-option-group>
          </el-select>
        </div>

        <div v-if="offer.offerType === OFFER_TYPE.PAUSE">
          <p class="mt-4 text-sm font-semibold leading-5 text-gray-700 mb-2">Allow subscriptions to pause up to…</p>
          <el-select
            :value="offer.pauseConfig.maxPauseLength"
            @change="(maxPauseLength) => $emit('update', { pauseConfig: { maxPauseLength, pauseInterval: 'MONTH' } })"
            placeholder="Select"
            class="w-full"
          >
            <el-option v-for="item in pauseOptions" :key="item.value" :label="item.label" :value="item.value"> </el-option>
          </el-select>
        </div>
        <!-- Trial Extension Period Field -->
        <div v-if="offer.offerType === OFFER_TYPE.TRIAL_EXTENSION">
          <p class="mt-4 text-sm font-semibold leading-5 text-gray-700 mb-2">Extend trial by…</p>
          <el-input
            type="numeric"
            placeholder="14"
            @input="(trialExtensionDays) => $emit('update', { trialExtensionConfig: { trialExtensionDays } })"
            :value="offer.trialExtensionConfig.trialExtensionDays"
            ><template slot="append">day(s)</template></el-input
          >
        </div>
        <!-- Change Plans Options -->
        <div v-if="offer.offerType === OFFER_TYPE.PLAN_CHANGE">
          <p class="mt-4 text-sm font-semibold leading-5 text-gray-700 mb-2">Allow customers to change to the following plans…</p>
          <!-- Plan Value Options -->
          <div class="flex space-x-4">
            <el-select
              filterable
              allow-create
              class="flex-grow"
              :value="offer.planChangeConfig.options"
              @change="(plan) => $emit('update', { planChangeConfig: { options: plan } })"
              :multiple="true"
            >
              <el-option-group label="Current">
                <el-option
                  v-for="plan in groupedPriceOptions.active"
                  :key="plan.id.toString()"
                  :value="plan.id.toString()"
                  :label="priceDescription(plan)"
                >
                  <div class="inline-block w-full">
                    <div class="flex justify-between items-center pt-2">
                      <p class="mr-4 flex-grow text-brand-blue font-medium text-xs">
                        {{ priceDescription(plan) }}
                      </p>
                      <p class="text-gray-600 text-xs mr-6">
                        {{ plan.nickname || plan.id }}
                      </p>
                    </div>
                  </div>
                </el-option>
              </el-option-group>
            </el-select>
          </div>
        </div>
      </div>
    </div>
    <!-- if shown in the "OFFERS" section, as opposed to the Offer Step -->
    <span class="absolute -bottom-7 left-0" v-if="surveyOffer">
      <AIReworkButton
        @rework="$emit('rework', choice)"
        :blueprintGuid="blueprintGuid"
        :surveyChoiceGuid="choice.guid"
        :loading="loading"
        :canCancel="canCancelAI"
      />
    </span>
  </div>
</template>

<script>
import { OFFER_TYPE, DISCOUNT_TYPE, BRAINTREE_PAUSE_DISCOUNT_ID, AI_JOB_STATUS, buildCustomCouponId } from '@/store/shared';
import Editor from '@/components/UI/Editor.vue';
import { range } from 'd3-array';
import { mapGetters } from 'vuex';
import CouponSelector from '@/components/UI/CouponSelector.vue';
import { FETCH_ORG_COUPONS } from '@/store/action-types';
import { jobAffectsSurveyChoice, generatingChoice } from '@/helpers/ai-jobs';
import AIReworkButton from './AIReworkButton.vue';

const maxAllowedPauseLength = 12;
const pauseOptions = range(1, maxAllowedPauseLength + 1).map((l) => ({
  value: l,
  label: `${l} ${l === 1 ? 'month' : 'months'}`,
}));

export default {
  name: 'OfferCardNew',
  props: {
    choice: {
      type: Object,
      required: true,
    },
    surveyOffer: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Editor,
    CouponSelector,
    AIReworkButton,
  },
  data() {
    return {
      OFFER_TYPE,
      DISCOUNT_TYPE,
      discountSelected: null,
      pauseOptions,
      reloadingCoupons: false,
      BRAINTREE_PAUSE_DISCOUNT_ID,
    };
  },
  computed: {
    ...mapGetters(['provider', 'isPaddleClassic', 'orgCanPause', 'orgSupportsCoupons', 'missingPauseCoupon', 'hasPauseCoupon', 'aiJobsInProgress']),
    blueprint() {
      return this.$store.getters.currentBlueprint;
    },
    blueprintGuid() {
      return this.blueprint.guid;
    },
    // Does some AI job affect this step somehow? (choice-rework or whole-blueprint job). If so, show loading state.
    loading() {
      if (this.aiJobsInProgress && this.aiJobsInProgress.length > 0) {
        return this.aiJobsInProgress.some((job) => jobAffectsSurveyChoice(job, this.blueprintGuid, this.choice.guid));
      }
      return false;
    },
    // Does some AI job affect this step specifically? (only step-rework). If so, show Cancel button.
    canCancelAI() {
      return this.aiJobsInProgress.some((job) => generatingChoice(job, this.blueprintGuid, this.choice.guid));
    },
    offer() {
      return this.choice.offer;
    },
    header() {
      // header nested under offer on survey offer
      if (this.surveyOffer) {
        return this.offer.header;
      }
      return this.choice.header;
    },
    description() {
      // description nested under offer on survey offer
      if (this.surveyOffer) {
        return this.offer.description;
      }
      return this.choice.description;
    },
    discountLabel() {
      let label = 'Discount';
      if (this.provider === 'stripe') label = 'Stripe Coupon';
      if (this.provider === 'braintree') label = 'Braintree Discount';
      if (this.provider === 'chargebee') label = 'Chargebee Discount';
      if (this.provider === 'paddle') {
        if (this.isPaddleClassic) {
          label = 'Custom Discount';
        } else {
          label = 'Paddle Coupon';
        }
      }
      return label;
    },
    offerTypes() {
      const types = [];
      types.push({ value: OFFER_TYPE.DISCOUNT, label: `Apply ${this.discountLabel}` });
      if (this.orgCanPause) {
        types.push({ value: OFFER_TYPE.PAUSE, label: 'Pause Subscription' });
      }
      types.push({ value: OFFER_TYPE.TRIAL_EXTENSION, label: 'Trial Extension' });
      types.push({ value: OFFER_TYPE.PLAN_CHANGE, label: 'Switch Subscription Plan' });
      types.push({ value: OFFER_TYPE.CONTACT, label: 'Contact Us' });
      types.push({ value: OFFER_TYPE.REDIRECT, label: 'Send to Custom Page' });
      return types;
    },
    discountTypeLabel() {
      const labels = {};
      labels[DISCOUNT_TYPE.AMOUNT] = 'Fixed Amount Discount';
      if (this.provider !== 'braintree') labels[DISCOUNT_TYPE.PERCENT] = 'Percentage Discount';
      return labels;
    },
    discountOptions() {
      return this.$store.state.stripeData.coupons;
    },
    coupons() {
      if (this.provider === 'braintree') {
        return this.$store.getters.coupons.filter((c) => c.id !== BRAINTREE_PAUSE_DISCOUNT_ID);
      }
      return this.$store.getters.coupons;
    },
    couponsByType() {
      if (!this.coupons) {
        return [];
      }
      // prettier-ignore
      const groupOptions = [
        { key: `${DISCOUNT_TYPE.PERCENT}-once`, label: 'One-time Percentage Discount', options: [], discountType: DISCOUNT_TYPE.PERCENT },
        { key: `${DISCOUNT_TYPE.PERCENT}-repeating`, label: 'Multi-month Percentage Discount', options: [], discountType: DISCOUNT_TYPE.PERCENT },
        { key: `${DISCOUNT_TYPE.PERCENT}-forever`, label: 'Forever Percentage Discount', options: [], discountType: DISCOUNT_TYPE.PERCENT },
        { key: `${DISCOUNT_TYPE.AMOUNT}-once`, label: 'One-time Fixed Amount Discount', options: [], discountType: DISCOUNT_TYPE.AMOUNT },
        { key: `${DISCOUNT_TYPE.AMOUNT}-repeating`, label: 'Multi-month Fixed Amount Discount', options: [], discountType: DISCOUNT_TYPE.AMOUNT },
        { key: `${DISCOUNT_TYPE.AMOUNT}-forever`, label: 'Forever Fixed Amount Discount', options: [], discountType: DISCOUNT_TYPE.AMOUNT },
      ];
      this.coupons.forEach((coupon) => {
        const group = groupOptions.find((o) => o.key === `${coupon.discountType}-${coupon.duration}`);
        group.options.push(coupon);
      });
      const filteredOptions = groupOptions.filter((g) => g.options.length);
      filteredOptions.forEach((group) => {
        group.options.sort((a, b) => {
          if (group.discountType === DISCOUNT_TYPE.PERCENT) {
            if (a.percentOff === b.percentOff) {
              return a.months - b.months;
            }
            return a.percentOff - b.percentOff;
          }
          if (a.amountOff === b.amountOff) {
            return a.months - b.months;
          }
          return a.amountOff - b.amountOff;
        });
      });
      return filteredOptions;
    },
    priceOptions() {
      return this.$store.state.stripeData.prices || [];
    },
    groupedPriceOptions() {
      return {
        active: this.priceOptions.filter((p) => p.active),
        inactive: this.priceOptions.filter((p) => !p.active),
      };
    },
  },
  methods: {
    priceDescription(price) {
      return this.$store.getters.stripePriceDescription(price.id, false);
    },
    handleCouponSelect(couponId) {
      this.$emit('update', { discountConfig: { couponId } });
    },
    updateCustomDiscount(customDuration, customAmount) {
      const discountConfig = {
        customAmount,
        customDuration,
      };
      const couponId = buildCustomCouponId(discountConfig);
      discountConfig.couponId = couponId;
      this.$emit('update', { discountConfig });
    },
    updateCustomDuration(customDuration) {
      this.updateCustomDiscount(customDuration, this.choice.offer?.discountConfig?.customAmount);
    },
    updateCustomAmount(customAmount) {
      this.updateCustomDiscount(this.choice.offer?.discountConfig?.customDuration, customAmount * 100);
    },
    async reloadCoupons() {
      this.reloadingCoupons = true;
      await this.$store.dispatch(FETCH_ORG_COUPONS, true);
      if (!this.hasPauseCoupon) {
        this.$message({
          type: 'warning',
          message: 'Could not find the CHURNKEY_PAUSE discount. Please create it in your Braintree dashboard.',
          duration: 5000,
        });
      }
      this.reloadingCoupons = false;
    },
  },
};
</script>
